import React from 'react';
import { Input, InputNumber, Button, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconTrash, IconLock, IconAlertTriangle } from '@tabler/icons-react';
import { IBookingQuoteRevisionLine } from '../../interfaces/heavyDutyMachinery/IBooking';
import {
  IQuoteDraftEditLine,
  editLineAmount,
  editLinesTotal,
  emptyEditLine,
  isMetered,
  repriced,
  estimatedCount,
} from '../../helpers/quoteDraftHelper';
import { lockedLineHintKey } from '../../helpers/visitFeeHelper';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { TEAL, SURFACE_1_SOFT } from '../../theme/creamyTokens';
import QuotePriceSourcePill from './QuotePriceSourcePill';

const { Text } = Typography;

interface QuoteLineEditorProps {
  lines: IQuoteDraftEditLine[];
  onChange: (lines: IQuoteDraftEditLine[]) => void;
  /**
   * Lines the viewer can see but not touch, because somebody else gets paid
   * for them. Shown, not hidden: the total being built includes them, and a
   * padlock with a reason is what stops "why can't I change this?" becoming a
   * support message.
   */
  lockedLines?: IBookingQuoteRevisionLine[];
  currency: string;
  /** Slotted between the lines and the total -- the assistant's notes, typically. */
  footer?: React.ReactNode;
}

/**
 * The itemised quote editor: a list of lines, each either one flat figure or a
 * quantity at a unit price, plus whatever is locked and the running total.
 *
 * Lives on its own because it now has three callers coming (the host's
 * counter-offer, the assistant's draft, and voice editing on top of either),
 * and because the rounding here has to match the server's to the cent -- one
 * place to get that right is worth more than three copies that nearly do.
 *
 * It owns no state. The lines belong to whoever opened it, which is what lets
 * the assistant replace its own lines from outside without the editor having
 * to know the assistant exists.
 */
const QuoteLineEditor: React.FC<QuoteLineEditorProps> = ({ lines, onChange, lockedLines = [], currency, footer }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const money = (value: number) => formatCurrency(value, currency, i18n.language);

  const lockedTotal = lockedLines.reduce((sum, line) => sum + line.amount, 0);
  const total = lockedTotal + editLinesTotal(lines);
  const guessed = estimatedCount(lines);

  const patch = (key: string, changes: Partial<IQuoteDraftEditLine>) =>
    onChange(lines.map((line) => (line.key === key ? repriced(line, changes) : line)));

  // One button both ways: a line switched by mistake has to be switchable
  // back, and "3 h x 45" vs "135 y me entiendes" is a judgement call that
  // belongs to whoever is writing the quote, not to us.
  const toggleMetered = (line: IQuoteDraftEditLine) =>
    onChange(
      lines.map((current) =>
        current.key === line.key
          ? isMetered(current)
            ? { ...current, amount: editLineAmount(current), quantity: null, unitPrice: null, unit: undefined }
            : { ...current, quantity: 1, unit: current.unit ?? 'ud', unitPrice: current.amount ?? 0 }
          : current
      )
    );

  const numberStyle: React.CSSProperties = { width: '100%' };

  return (
    <>
      {lockedLines.map((line, index) => (
        <div
          key={`locked-${index}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            marginBottom: 8,
            borderRadius: 12,
            background: token.colorFillQuaternary,
          }}
        >
          <IconLock size={15} style={{ flexShrink: 0, color: token.colorTextTertiary }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: 13 }}>{line.title}</Text>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              {/* Per line, not one sentence for all of them: the standing hint
                  says somebody else charges this, which is flatly wrong about the
                  visit -- that money is this professional's own. */}
              {translate(t, lockedLineHintKey(line))}
            </Text>
          </div>
          <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{money(line.amount)}</Text>
        </div>
      ))}

      {guessed > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: '10px 12px',
            marginBottom: 10,
            borderRadius: 12,
            background: token.colorWarningBg,
          }}
        >
          <IconAlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2, color: token.colorWarning }} />
          <Text style={{ fontSize: 12, color: token.colorWarningText }}>
            {translate(t, TRANSLATIONS.QUOTE_EDITOR_ESTIMATED_WARNING, { count: guessed })}
          </Text>
        </div>
      )}

      {lines.map((line) => {
        const metered = isMetered(line);
        return (
          <div
            key={line.key}
            style={{
              padding: 12,
              marginBottom: 8,
              borderRadius: 12,
              border: `1px solid ${line.priceSource === 'Estimated' ? token.colorWarningBorder : token.colorBorderSecondary}`,
              background: token.colorBgContainer,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Input
                value={line.title}
                onChange={(e) => patch(line.key, { title: e.target.value })}
                placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_TITLE_PLACEHOLDER)}
                maxLength={120}
                style={{ flex: 1, fontWeight: 600 }}
              />
              <Button
                type="text"
                icon={<IconTrash size={16} />}
                onClick={() => onChange(lines.filter((l) => l.key !== line.key))}
                style={{ flexShrink: 0, color: token.colorTextTertiary }}
                aria-label={translate(t, TRANSLATIONS.QUOTE_EDITOR_REMOVE_LINE)}
              />
            </div>

            <Input
              value={line.description}
              onChange={(e) => patch(line.key, { description: e.target.value })}
              placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_DESCRIPTION_PLACEHOLDER)}
              maxLength={500}
              variant="borderless"
              style={{ marginTop: 2, paddingLeft: 0, fontSize: 12 }}
            />

            {metered ? (
              // Wraps rather than scrolls: on a phone the quantity and the
              // unit price end up on their own line, which is still readable,
              // where a horizontal scroller hides half the arithmetic.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <InputNumber
                  value={line.quantity}
                  onChange={(value) => patch(line.key, { quantity: value })}
                  min={0}
                  step={1}
                  precision={2}
                  inputMode="decimal"
                  placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_QUANTITY_PLACEHOLDER)}
                  style={{ ...numberStyle, width: 88 }}
                />
                <Input
                  value={line.unit}
                  onChange={(e) => patch(line.key, { unit: e.target.value })}
                  placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_UNIT_PLACEHOLDER)}
                  maxLength={16}
                  style={{ width: 64 }}
                />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ×
                </Text>
                {/* No min on the price: a negative line is a discount, which
                    is a legitimate thing to put in a quote. The server
                    enforces the real floor (never below what is owed to
                    somebody else). */}
                <InputNumber
                  value={line.unitPrice}
                  onChange={(value) => patch(line.key, { unitPrice: value })}
                  step={1}
                  precision={2}
                  inputMode="decimal"
                  addonAfter="€"
                  placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_UNIT_PRICE_PLACEHOLDER)}
                  style={{ ...numberStyle, width: 130 }}
                />
                <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                  {money(editLineAmount(line))}
                </Text>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <InputNumber
                  value={line.amount}
                  onChange={(value) => patch(line.key, { amount: value })}
                  step={1}
                  precision={2}
                  inputMode="decimal"
                  addonAfter="€"
                  placeholder={translate(t, TRANSLATIONS.QUOTE_EDITOR_AMOUNT_PLACEHOLDER)}
                  style={{ width: 130 }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <QuotePriceSourcePill source={line.priceSource} />
              <Button
                type="link"
                size="small"
                onClick={() => toggleMetered(line)}
                style={{ padding: 0, height: 'auto', fontSize: 11, marginLeft: 'auto' }}
              >
                {translate(t, metered ? TRANSLATIONS.QUOTE_EDITOR_TO_FLAT : TRANSLATIONS.QUOTE_EDITOR_TO_METERED)}
              </Button>
            </div>
          </div>
        );
      })}

      <Button block icon={<IconPlus size={15} />} onClick={() => onChange([...lines, emptyEditLine()])} style={{ borderRadius: 999, marginBottom: 14 }}>
        {translate(t, TRANSLATIONS.QUOTE_EDITOR_ADD_LINE)}
      </Button>

      {footer}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '10px 12px',
          borderRadius: 12,
          background: SURFACE_1_SOFT,
          marginBottom: 14,
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          {translate(t, TRANSLATIONS.QUOTE_EDITOR_TOTAL)}
        </Text>
        <Text strong style={{ fontSize: 18, color: TEAL, whiteSpace: 'nowrap' }}>
          {money(total)}
        </Text>
      </div>
    </>
  );
};

export default QuoteLineEditor;
